import { useEffect, useRef, useState } from 'react';
import { useRetrieveEntityList } from 'src/compass/shared/hooks/reactQuery/useRetrieveEntityList';
import { CasRHFSelect, CasEntitySelectProps } from 'src/compass/shared/components';
import { useFormContext } from 'react-hook-form';

// ----------------------------------------------------------------------

type IScheduleSelect = CasEntitySelectProps & {
  employeeId: string | undefined;
  name: string;
};

function useScheduleSelect(params: Record<string, any>, enabled: boolean = true) {
  const { data, isLoading } = useRetrieveEntityList({
    entity: 'Ins/CommSchedule/Select',
    params,
    enabled,
  });
  return [data?.result || [], isLoading];
}

export default function ScheduleSelect({ employeeId, name, ...otherProps }: IScheduleSelect) {
  const { setValue, watch } = useFormContext();

  const asOfDate = watch('asOf.asOfDate');

  const [options, isLoading] = useScheduleSelect({ servicingAgentId: employeeId }, !!employeeId);

  const [depsUpdated, setDepsUpdated] = useState(false);

  const employeeIdRef = useRef(employeeId);
  const asOfDateRef = useRef(asOfDate);

  // Monitor employeeId and asOfDate together
  useEffect(() => {
    if (
      (employeeIdRef.current && employeeIdRef.current !== employeeId) ||
      asOfDateRef.current !== asOfDate
    ) {
      setDepsUpdated(true);
    }
  }, [employeeId, asOfDate]);

  // This will now only run once either id or date are updated
  useEffect(() => {
    if (depsUpdated) {
      if (employeeIdRef.current !== employeeId && asOfDateRef.current === asOfDate) {
        setValue(name, null);
      }
      employeeIdRef.current = employeeId;
      asOfDateRef.current = asOfDate;
      // Reset depsUpdated
      setDepsUpdated(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [depsUpdated]);

  return <CasRHFSelect {...otherProps} name={name} loading={isLoading} options={options} />;
}
